import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import { projectKeys } from '@/lib/queryKeys';
import { projectEndpoints } from '@/lib/api/endpoints';
import type { ProjectJobDependency } from '@/types/project';

export function useProjectDependencies(projectId: string | undefined) {
  return useApiQuery<ProjectJobDependency[]>({
    queryKey: projectKeys.dependencies(projectId ?? ''),
    queryFn: () =>
      apiGet<ProjectJobDependency[]>(operationsClient, projectEndpoints.dependencies(projectId!)),
    enabled: Boolean(projectId),
  });
}

export function useCreateDependency(projectId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    ProjectJobDependency,
    { predecessorJobId: string; successorJobId: string; lagDays?: number }
  >({
    mutationFn: (input) =>
      apiPost<ProjectJobDependency, typeof input>(
        operationsClient,
        projectEndpoints.dependencies(projectId),
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.dependencies(projectId),
      });
      toast.success('Dependency added');
    },
  });
}

export function useDeleteDependency(projectId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<void, string>({
    mutationFn: (dependencyId) =>
      apiDelete<void>(operationsClient, projectEndpoints.dependency(projectId, dependencyId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.dependencies(projectId),
      });
      toast.success('Dependency removed');
    },
  });
}
